import {
  createTinyHealthApiClient,
  ApiClientConfig,
  UserAttributes as ApiClientUserAttributes,
  SignInPayload,
  SignUpPayload,
  ConfirmSignUpPayload,
  ForgotPasswordPayload,
  ForgotPasswordSubmitPayload,
  CompleteNewPasswordPayload,
  ResendSignUpPayload,
  RequestMonitoringMetadata,
} from '@repo/api-client';
import { urls } from '@repo/app-constants';
import { ENVIRONMENT, USE_SANDBOX, API_BASE_URL } from 'assets/env_constants';
// UserAttributes is imported from store/user below, aliasing the client's one to avoid conflict if needed.
import { AUTOLOGOUT_REASONS } from 'assets/constants';
import { signOutUser } from 'services/auth/useSignOut';
import store from 'store/store';
import { toast } from 'react-toastify';
import { getJWTToken, deleteJWTToken } from 'utils/jwt';
import { deleteImpersonationCookie } from 'utils/impersonation';
// Keep AxiosError for now, as UI components might still catch it before client's named errors propagate
import { AxiosError } from 'axios';
// Configuration for the API client
// SAFETY: The api-client's getBaseUrl() only uses defaultApiUrl (production) when 
// environment='production'. All other cases fall back to sandboxApiUrl for safety.
const apiClientConfig: ApiClientConfig = {
  environment: ENVIRONMENT,
  previewApiBaseUrl: API_BASE_URL,
  useSandbox: USE_SANDBOX === 'true',
  defaultApiUrl: urls.PRODUCTION_API_URL, // Only used when environment='production'
  sandboxApiUrl: urls.SANDBOX_API_URL,
  localDevelopmentApiUrl: urls.LOCAL_DEVELOPMENT_API_URL, // For local dev hitting sandbox  backend
  getAppOrigin: async () => {
    // In browser, use window.location.origin
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return null;
  },
  getToken: () => getJWTToken(),
  getImpersonateId: () => store.getState().user.impersonateId || null,
  onUnauthenticated: async () => {
    if (window.queryClient) {
      const username = store.getState().user.username;
      await window.queryClient.removeQueries({
        predicate: query =>
          username ? query.queryKey.includes(username) : false,
      });
    }
    if (store.getState().user.isAuthenticated) {
      await store.dispatch(
        signOutUser({ autoLogoutReason: AUTOLOGOUT_REASONS.EXPIRED }),
      );
    }
  },
  onForbidden: () => {
    toast.error('You do not have permission to perform this action');
  },
  showErrorToast: (message: string) => {
    toast.error(message);
  },
};
export const getApiClientConfig = () => apiClientConfig;
const apiClient = createTinyHealthApiClient(apiClientConfig);

// Re-export UserAttributes from the store if it's different or used elsewhere,
// or ensure types are compatible. For Auth.signUp, ApiClientUserAttributes is used by the client.
export type { UserAttributes } from 'store/user';

export const Auth = {
  signIn: async (email: string, password: string) => {
    // The client's signIn method already throws NotAuthorizedException or UserNotConfirmedException
    return apiClient.auth.signIn({ email, password } as SignInPayload);
  },
  signOut: async () => {
    try {
      await apiClient.auth.signOut();
    } catch (error) {
      // Log or handle sign-out specific error, though client should manage general errors
      console.error('Error during API sign out:', error);
      // Still proceed with local cleanup
    }
    deleteJWTToken();
    deleteImpersonationCookie({ scope: 'host-only' });
    // Optionally, could also call onUnauthenticated actions here if not covered by API response
    return null;
  },
  signUp: async (
    data: {
      email: string;
      password: string;
    } & Partial<ApiClientUserAttributes>,
  ) => {
    // The client's signUp method throws UsernameExistsException or InvalidPasswordException
    return apiClient.auth.signUp(data as SignUpPayload);
  },
  confirmSignUp: async (email: string, code: string) => {
    // The client's confirmSignUp throws CodeMismatchException
    return apiClient.auth.confirmSignUp({
      email,
      code,
    } as ConfirmSignUpPayload);
  },
  forgotPassword: async (email: string, method: string) => {
    // The client's forgotPassword throws UserNotFoundException
    return apiClient.auth.forgotPassword({ email, method } as ForgotPasswordPayload);
  },
  forgotPasswordSubmit: async (
    email: string,
    code: string,
    password: string,
  ) => {
    // The client's forgotPasswordSubmit throws CodeMismatchException or InvalidPasswordException
    return apiClient.auth.forgotPasswordSubmit({
      email,
      code,
      password,
    } as ForgotPasswordSubmitPayload);
  },
  completeNewPassword: async (password: string) => {
    return apiClient.auth.completeNewPassword({
      password,
    } as CompleteNewPasswordPayload);
  },
  resendSignUp: async (email: string) => {
    return apiClient.auth.resendSignUp({ email } as ResendSignUpPayload);
  },
};

export const API = {
  get: async <TResponse = any>(
    apiName: string, // e.g., 'tinyhealthapi' or 'someotherapi'
    url: string,
    data?: Record<string, unknown>,
    requireAuth?: boolean,
    headers?: Record<string, string>,
    monitoringMetadata?: RequestMonitoringMetadata,
  ): Promise<TResponse> => {
    const auth =
      requireAuth !== undefined ? requireAuth : apiName === 'tinyhealthapi';
    return apiClient.api.get<TResponse>(
      url,
      data,
      auth,
      headers,
      monitoringMetadata,
    );
  },
  post: async <TResponse = any>(
    apiName: string,
    url: string,
    data?: Record<string, unknown> | { body: Record<string, unknown> }, // Accommodate data or data.body pattern
    requireAuth?: boolean,
    headers?: Record<string, string>,
    monitoringMetadata?: RequestMonitoringMetadata,
  ): Promise<TResponse> => {
    const auth =
      requireAuth !== undefined ? requireAuth : apiName === 'tinyhealthapi';
    const body = (data as any)?.body || data;
    return apiClient.api.post<TResponse>(
      url,
      body,
      auth,
      headers,
      monitoringMetadata,
    );
  },
  put: async <TResponse = any>(
    apiName: string,
    url: string,
    data?: Record<string, unknown> | { body: Record<string, unknown> },
    requireAuth?: boolean,
    headers?: Record<string, string>,
    monitoringMetadata?: RequestMonitoringMetadata,
  ): Promise<TResponse> => {
    const auth =
      requireAuth !== undefined ? requireAuth : apiName === 'tinyhealthapi';
    const body = (data as any)?.body || data;
    return apiClient.api.put<TResponse>(
      url,
      body,
      auth,
      headers,
      monitoringMetadata,
    );
  },
  patch: async <TResponse = any>(
    apiName: string,
    url: string,
    data?: Record<string, unknown> | { body: Record<string, unknown> },
    requireAuth?: boolean,
    headers?: Record<string, string>,
    monitoringMetadata?: RequestMonitoringMetadata,
  ): Promise<TResponse> => {
    const auth =
      requireAuth !== undefined ? requireAuth : apiName === 'tinyhealthapi';
    const body = (data as any)?.body || data;
    return apiClient.api.patch<TResponse>(
      url,
      body,
      auth,
      headers,
      monitoringMetadata,
    );
  },
  del: async <TResponse = any>(
    apiName: string,
    url: string,
    data?: Record<string, unknown>, // DELETE typically uses URL params or empty body
    requireAuth?: boolean,
    headers?: Record<string, string>,
    monitoringMetadata?: RequestMonitoringMetadata,
  ): Promise<TResponse> => {
    const auth =
      requireAuth !== undefined ? requireAuth : apiName === 'tinyhealthapi';
    return apiClient.api.del<TResponse>(
      url,
      data,
      auth,
      headers,
      monitoringMetadata,
    );
  },
};

// Ensure window.queryClient is available where this module is initialized/used if still relying on it for onUnauthenticated.

// Example of re-exporting AxiosError if other parts of the app expect to catch it specifically,
// though ideally they'd catch the named errors from the client.
export { AxiosError };
